/**
 * Appeal bond generation (Lloyd, Aug 19 2026: *"Yes, build away"*).
 *
 * Two calls, and the split matters. `readiness` asks what is missing WITHOUT producing anything, so
 * the page can tell staff to fill in a county or a cause number before they press a button. Only
 * `generate` produces a document, and it produces one that goes straight to Lloyd to sign.
 */

export interface AppealBondReadinessDto {
  courtType: 'municipal' | 'jp';
  courtFineCents: number | null;
  /** Plain-English reasons the bond cannot be generated. Empty when it can. */
  blockers: string[];
  canGenerate: boolean;
}

export interface AppealBondResultDto {
  fileName: string;
  courtType: 'municipal' | 'jp';
  /** The judgment sum, words and figures, exactly as printed in paragraph one. */
  judgmentAmount: string;
  /** Twice the judgment — the sum sworn to in paragraph two. */
  suretyAmount: string;
  /** Who it was emailed to, or null when delivery failed. */
  sentTo: string | null;
  /** Set when the PDF generated but the email did not go. The document is not lost. */
  deliveryError: string | null;
  pdfBase64: string;
}

/** Thrown when the case is not ready. Carries the reasons so the caller can list them. */
export class AppealBondBlockedError extends Error {
  constructor(
    message: string,
    public readonly blockers: string[],
  ) {
    super(message);
    this.name = 'AppealBondBlockedError';
  }
}

export interface GenerateAppealBondInput {
  judgmentDate: string;
  signatureDate?: string | null;
  notaryDate?: string | null;
  /** Overrides the fine stored on the case, and is written back to it. */
  fineCents?: number | null;
}

export interface GenerateAttorneyBondInput {
  /** What the court set, in cents. Entered by a person; no rule computes it. */
  bondAmountCents: number;
  signatureDate: string;
  notaryDate?: string | null;
}

export interface AttorneyBondResultDto {
  fileName: string;
  courtType: 'municipal' | 'jp';
  /** The sum as the bond prints it, words and figures. Not doubled. */
  bondAmount: string;
  sentTo: string | null;
  /** What happens next, since the client signs before Lloyd does. */
  nextStep: string | null;
  deliveryError: string | null;
  pdfBase64: string;
}

/** A bond row as the case screen reads it. Dates are ISO strings or null. */
export interface CaseBondDto {
  CaseBond_guid: string;
  CaseBond_Type: 'APPEAL' | 'ATTORNEY' | string;
  CaseBond_AmountCents: number | null;
  CaseBond_FromCourtType: string | null;
  CaseBond_JudgmentDate: string | null;
  CaseBond_GeneratedAt: string;
  CaseBond_SignedAt: string | null;
  CaseBond_FiledAt: string | null;
  CaseBond_Note: string | null;
  CaseBond_WithdrawalMotionGeneratedAt: string | null;
  CaseBond_WithdrawalGround: string | null;
  CaseBond_WithdrawalMotionFiledAt: string | null;
  CaseBond_SuretyReleasedAt: string | null;
  CaseBond_SuretyReleasedBy: string | null;
}

export interface CaseBondsDto {
  bonds: CaseBondDto[];
  courtLevel: {
    level: 'JP_OR_MUNICIPAL' | 'COURT_OF_RECORD';
    reason: string;
    hasUnfiledAppealBond: boolean;
  };
  /**
   * The bonds the firm is still personally standing surety on — filed and not yet released.
   *
   * A different question from the court level, off the same rows: the level is about the client's
   * court, this is about Lloyd's own liability, and an attorney bond counts here while it never
   * counts there.
   */
  liveSuretyBonds: Array<{ guid?: string; type: string }>;
}

/** The dates a person records by hand. Every one of them can be cleared, because a wrong one must be undoable. */
export interface UpdateBondInput {
  CaseBond_SignedAt?: string | null;
  CaseBond_FiledAt?: string | null;
  CaseBond_AmountCents?: number | null;
  CaseBond_Note?: string | null;
  CaseBond_WithdrawalMotionFiledAt?: string | null;
  CaseBond_SuretyReleasedAt?: string | null;
}

export default class BondApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  /** Every bond on the case, with the court level and the live surety exposure beside them. */
  static async list(ticketGuid: string): Promise<CaseBondsDto> {
    const response = await fetch(
      `${BondApi.host()}/api/v1/manager/ticket/${ticketGuid}/bonds`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to load the bonds on this case');
    return response.json();
  }

  /**
   * Record a date against a bond.
   *
   * Two of these change what the firm is on the hook for: `CaseBond_FiledAt` moves the case to a
   * court of record, and `CaseBond_SuretyReleasedAt` ends the personal liability. Neither is ever a
   * side effect of generating a document — a person says so, and the server records who.
   */
  static async update(bondGuid: string, input: UpdateBondInput): Promise<CaseBondDto> {
    const response = await fetch(`${BondApi.host()}/api/v1/manager/bond/${bondGuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to update the bond');
    }
    return response.json();
  }

  static async readiness(
    ticketGuid: string,
    judgmentDate?: string,
  ): Promise<AppealBondReadinessDto> {
    const query = judgmentDate ? `?judgmentDate=${encodeURIComponent(judgmentDate)}` : '';
    const response = await fetch(
      `${BondApi.host()}/api/v1/manager/ticket/${ticketGuid}/appeal-bond/readiness${query}`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to check whether this case is ready for a bond');
    return response.json();
  }

  static async generate(
    ticketGuid: string,
    input: GenerateAppealBondInput,
  ): Promise<AppealBondResultDto> {
    const response = await fetch(
      `${BondApi.host()}/api/v1/manager/ticket/${ticketGuid}/appeal-bond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 422) {
      const err = await response.json().catch(() => ({}));
      throw new AppealBondBlockedError(
        err.message || 'This case is not ready for a bond.',
        Array.isArray(err.blockers) ? err.blockers : [],
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.details || err.error || err.message || 'Failed to generate the bond');
    }
    return response.json();
  }

  /**
   * The attorney bond, which differs from the appeal bond in the two ways that matter here.
   *
   * The amount is **entered, never computed**. Lloyd: *"Courts determine it. Varies
   * significantly."* The appeal bond's double-the-fine rule is his rule for appeal bonds and does
   * not apply, so there is no fine field and nothing is doubled on the way through.
   *
   * And the **client signs first**, so the finished document goes to the internal mailbox for a
   * person to send for signature rather than straight to Lloyd.
   */
  static async generateAttorneyBond(
    ticketGuid: string,
    input: GenerateAttorneyBondInput,
  ): Promise<AttorneyBondResultDto> {
    const response = await fetch(
      `${BondApi.host()}/api/v1/manager/ticket/${ticketGuid}/attorney-bond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 422) {
      const err = await response.json().catch(() => ({}));
      throw new AppealBondBlockedError(
        err.message || 'This case is not ready for a bond.',
        Array.isArray(err.blockers) ? err.blockers : [],
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.details || err.error || err.message || 'Failed to generate the bond');
    }
    return response.json();
  }
}
